import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { PageSubTitle } from 'Components/Titles';
import { RestartPlanChurned } from 'Features/MyAccount/RestartPlanChurned';
import { timestampToDate } from 'Utils/Format';
import { trackEvent, TRACKING_ENUMS } from 'Utils/Analytics';
import { PlanSelectionModal } from 'Features/Plans/PlanSelection/PlanSelection';
import { getPlanFromList } from 'Utils/Plans';

export const WebSubscription = ({
  isPauseEnabled,
  paymentMethods,
  routes,
  subTitle,
  onRestartPlan,
  subscription,
  toggleResumePlanModal,
  trackEditAccount,
  handleCancelPauseSubscription,
  nextBillInfo,
  activePlanSimplifiedName,
  activePlan,
  allPlans,
  openPlanSelectModal = false,
  navigate,
  state,
}) => {
  const {
    isChangePlanAvailable,
    isChurnedRestartAvailable,
    restartChurnedUserDetails,
    hasSubscription,
    hasActiveSubscription,
    pauseStartDate,
    pauseEndDate,
    isPendingPause,
    isPaused,
    activeSubscriptionPlanName,
    activeSubscriptionId,
    validityTill,
    nextSubscription,
    purchaseType,
  } = subscription;

  let modalPlans = allPlans;
  if ((!allPlans || allPlans.length < 1) && activePlan) {
    modalPlans = [activePlan];
  }

  const { hasGiftingPayment } = paymentMethods;
  // DCGCARE-792 - customers who have a gift subscription, active or pending active,
  // are not able to Upgrade/Change Plan at this time
  const showChangePlanLink = isChangePlanAvailable && !hasGiftingPayment;

  const [showPlanSelectModal, setShowPlanSelectModal] = useState(openPlanSelectModal);

  const renderPauseMessage = () => {
    const paused = <span className="pause">PAUSED</span>;
    const pauseDate = <span className="date">{timestampToDate(pauseStartDate)}</span>;
    const resumeDate = <span className="date">{timestampToDate(pauseEndDate)}</span>;

    if (isPendingPause) {
      return (
        <div className="pausedDescription">
          Your Fox Nation subscription will be {paused} on {pauseDate} and will automatically resume on {resumeDate}.{' '}
          You can still access your Fox Nation content until the PAUSED start date.
        </div>
      );
    }

    if (isPaused) {
      return (
        <div className="pausedDescription">
          Your Fox Nation subscription is {paused} and will be reactivated on {resumeDate}. Resume now and continue
          enjoying your favorite shows. To Change Plan, resume account first.
        </div>
      );
    }

    return null;
  };

  const handleResumePlanClick = () => {
    const { planPrice } = nextBillInfo;
    const pageTraits = {
      page_start_source: 'my account',
      page_plan_name: activeSubscriptionPlanName,
      page_plan_price: planPrice,
      page_plan_id: activeSubscriptionId,
    };
    trackEvent(TRACKING_ENUMS.EVENT_ACCOUNT_RESUME_STARTED, pageTraits);
    toggleResumePlanModal(true);
  };

  const handleChangePlanClick = () => {
    trackEditAccount('Change Plan');
    setShowPlanSelectModal(true);
  };

  const onClosePlanSelectModal = () => {
    setShowPlanSelectModal(false);
    if (state) {
      const { showPlanSelectModal, ...newState } = state;
      navigate('', { state: newState, replace: true });
    }
  };

  return (
    <div className="sectionFlex membershipSection">
      <div className="flexGrowOne settingsSectionLeft">
        <PageSubTitle text={subTitle} />
      </div>
      <div
        className={`sectionFlex flexDirCol flexGrowTwo settingsSectionRight ${
          isPendingPause || isPaused ? 'paused' : ''
        }`}
      >
        <div className="sectionFlex">
          <div className="flexGrowThree">
            {isChurnedRestartAvailable && (
              <div className="sectionFlex restartChurnedUserDetails">
                <RestartPlanChurned {...restartChurnedUserDetails} />
              </div>
            )}
            {hasSubscription && <div className="subscriptionName">{activePlanSimplifiedName}</div>}
          </div>
          <div className="flexGrowOne">
            {isChurnedRestartAvailable && !subscription.isPendingCancel && (
              <div className="block text-right">
                <Link
                  to={routes.signupPlans}
                  className="profileLink cursorPointer restartChurnedPlan"
                  onClick={() => trackEditAccount('Resubscribe')}
                >
                  Resubscribe
                </Link>
              </div>
            )}
            {subscription.isPendingCancel && !isChurnedRestartAvailable && (
              <div className="block text-right">
                <span
                  role="button"
                  className={`profileLink cursorPointer`}
                  onClick={() => {
                    onRestartPlan();
                    trackEditAccount('Restart Plan');
                  }}
                >
                  Restart Plan
                </span>
              </div>
            )}
            {showChangePlanLink && (
              <div className="block text-right">
                <span className="profileLink" onClick={handleChangePlanClick}>
                  Change Plan
                </span>
              </div>
            )}
            {isPauseEnabled && !isPendingPause && (
              <div className="block text-right">
                <Link
                  to={routes.pauseSubscription}
                  className="profileLink"
                  onClick={() => trackEditAccount('Pause Plan')}
                >
                  Pause Plan
                </Link>
              </div>
            )}
            {isPaused && (
              <button type="button" className="resumeButton" onClick={handleResumePlanClick}>
                Resume Plan
              </button>
            )}
            {hasActiveSubscription && purchaseType !== 'coupon' && (
              <div className="block text-right">
                <Link to={routes.cancelPlan} className="blackLink" onClick={() => trackEditAccount('Cancel Plan')}>
                  Cancel Plan
                </Link>
              </div>
            )}
          </div>
        </div>
        {(isPendingPause || isPaused) && <div className="sectionFlex sectionDivider">{renderPauseMessage()}</div>}
        {isPendingPause && (
          <div className="sectionFlex cancelPause">
            <span role="button" className="profileLink" onClick={handleCancelPauseSubscription}>
              Cancel Pause Request
            </span>
          </div>
        )}
      </div>
      <PlanSelectionModal
        show={showPlanSelectModal && modalPlans.length > 0}
        currentPlan={activePlan}
        upcomingPlan={nextSubscription ? getPlanFromList(nextSubscription.serviceID, allPlans) : null}
        validityTill={validityTill}
        plans={modalPlans}
        onClose={onClosePlanSelectModal}
      />
    </div>
  );
};
